// /tools — the Tools hub. Gives every free tool + the playground a single home
// and the nav entry (the IA fix). Fieldbook language on design-tokens.css.
import { PageMeta } from "@/components/PageMeta";
import { canonicalUrl } from "@/lib/canonical";
import FieldHeader from "@/components/field/FieldHeader";
import FieldFooter from "@/components/field/FieldFooter";
import { trackGA4Event } from "@/lib/analytics";
import "@/styles/design-tokens.css";
import "./tools-hub.css";

interface Tool {
  name: string;
  desc: string;
  href?: string;
  icon: string;
  iconBg: string;
  badge: "free" | "dev" | "soon";
  badgeText: string;
}

const TOOLS: Tool[] = [
  { name: "NPI Lookup", desc: "Look up any provider by NPI — name, specialty, and practice address, enriched from CMS NPPES.", href: "/tools/npi-lookup", icon: "🩺", iconBg: "#e4eefb", badge: "free", badgeText: "Free · no sign-up" },
  { name: "API Playground", desc: "Try any FHIRfly endpoint live in the browser — pick a data type, hit Run, see the real response.", href: "/tools/api-playground", icon: "⚡", iconBg: "#fde7f1", badge: "dev", badgeText: "Live API" },
  { name: "Claims Denial Calculator", desc: "Estimate preventable denial losses and see which code edits recover the most revenue.", href: "/claims-calculator", icon: "📊", iconBg: "#e0f6f1", badge: "free", badgeText: "Free · no sign-up" },
  { name: "Rural Health Readiness", desc: "A guided assessment of your terminology, claims, and interoperability maturity.", href: "/rural-health/readiness", icon: "🏥", iconBg: "#fdf0d6", badge: "free", badgeText: "Free · no sign-up" },
  { name: "NDC Drug Lookup", desc: "Search a drug by NDC — brand, generic, manufacturer, and form. A focused sibling of the API.", icon: "💊", iconBg: "#fde7f1", badge: "soon", badgeText: "Coming soon" },
  { name: "ICD-10 Search", desc: "Find diagnosis codes by description and get human-readable detail and billability.", icon: "🔎", iconBg: "#e4eefb", badge: "soon", badgeText: "Coming soon" },
];

export default function ToolsHubPage() {
  return (
    <div className="ffly ffly--public tools-hub">
      <PageMeta
        title="Free Healthcare Tools — NPI lookup, API playground & more"
        description="Free, no-sign-up healthcare tools: NPI lookup, a live API playground, claims denial calculator, and rural health readiness — powered by FHIRfly's terminology API."
        canonical={canonicalUrl("/tools")}
      />

      <FieldHeader active="tools" />

      <div className="wrap">
        <section className="intro">
          <div className="crumb">Free tools</div>
          <h1>Useful tools, on us.</h1>
          <p className="lede">
            Practical, no-sign-up tools for billers, clinicians, and developers — each powered by the
            same FHIRfly terminology API. Use them free; when you need it programmatically, the API is one step away.
          </p>
        </section>

        <section className="grid">
          {TOOLS.map((t) => {
            const inner = (
              <>
                <div className="top">
                  <span className="ic" style={{ background: t.iconBg }}>{t.icon}</span>
                  <span className={`badge ${t.badge}`}>{t.badgeText}</span>
                </div>
                <h3>{t.name}</h3>
                <p>{t.desc}</p>
                {t.href ? <span className="go">Open tool →</span> : <span className="go" style={{ color: "var(--ffly-muted)" }}>In the works</span>}
              </>
            );
            return t.href ? (
              <a
                className="card"
                href={t.href}
                key={t.name}
                onClick={() => trackGA4Event("select_content", { content_type: "tool", item_id: t.name })}
              >{inner}</a>
            ) : (
              <div className="card disabled" key={t.name}>{inner}</div>
            );
          })}
        </section>
      </div>

      <FieldFooter variant="compact" />
    </div>
  );
}
